// Every GraphQL call in this app is a one-shot POST: TanStack Query owns the
// caching, graphql-request owns the wire protocol, and this module only binds
// endpoints, strips __typename from variables, and normalizes errors.

import { ClientError, GraphQLClient } from "graphql-request"

const DEFAULT_URL = "/v3/graphql"

// Deliberately `object` rather than Record<string, unknown>: callers describe
// their variables with interfaces, which TypeScript refuses to widen into an
// index signature. The transport only ever serializes them.
export type Variables = object

/** An executor bound to one endpoint. */
export function createClient(url: string) {
  // graphql-request parses its endpoint with `new URL(...)`, which rejects
  // relative paths, so resolve against the current origin first.
  const client = new GraphQLClient(new URL(url, window.location.origin).toString(), {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    requestMiddleware: (request) => ({
      ...request,
      variables: withoutTypename(request.variables) as Record<string, unknown>,
    }),
  })

  return async <TData>(
    document: string,
    variables: Variables = {},
  ): Promise<TData> => {
    try {
      return await client.request<TData>(document, variables as Record<string, unknown>)
    } catch (error) {
      throw normalize(error)
    }
  }
}

// ClientError.message is debug-oriented (it embeds the whole request and
// response as JSON) and several components show error.message to users, so
// rethrow with just the server's error messages.
function normalize(error: unknown): Error {
  if (error instanceof ClientError) {
    const messages = (error.response.errors ?? []).map((detail) => detail.message)
    return new Error(
      messages.length > 0
        ? messages.join("\n")
        : `GraphQL request failed (HTTP ${error.response.status})`,
    )
  }

  return error instanceof Error ? error : new Error(String(error))
}

// Values that came back from a query and are being sent to a mutation carry
// __typename wherever the document selected it, and GraphQL input objects
// reject unknown fields. Only plain objects and arrays are walked, so Dates,
// Files and the like reach JSON.stringify untouched.
function withoutTypename(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(withoutTypename)
  if (value === null || typeof value !== "object") return value
  if (Object.getPrototypeOf(value) !== Object.prototype) return value

  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => key !== "__typename")
      .map(([key, nested]) => [key, withoutTypename(nested)]),
  )
}

export default createClient(DEFAULT_URL)
